import { IInputProperty } from '../interface/common.interface'

export const COLLABORATOR_ROLE_OWNER = 'owner' as const
export const COLLABORATOR_ROLE_EDITOR = 'editor' as const
export const COLLABORATOR_ROLE_DOER = 'doer' as const
export const COLLABORATOR_ROLE_UPDATER = 'updater' as const
export const COLLABORATOR_ROLE_VIEWER = 'viewer' as const
export const COLLABORATOR_ROLE_REMOVE = 'remove' as const

export const ACTING_ROLE_ADMIN = 'admin' as const

export type CollaboratorRole =
  | typeof COLLABORATOR_ROLE_OWNER
  | typeof COLLABORATOR_ROLE_EDITOR
  | typeof COLLABORATOR_ROLE_DOER
  | typeof COLLABORATOR_ROLE_UPDATER
  | typeof COLLABORATOR_ROLE_VIEWER

/** A collaboration role, or ADMIN acting without being in the collaboration list. */
export type ActingUserRole = CollaboratorRole | typeof ACTING_ROLE_ADMIN

export const ORDER_PROPERTY_DATA_TYPE_TEXT = 'text' as const
export const ORDER_PROPERTY_DATA_TYPE_STRING = 'string' as const
export const ORDER_PROPERTY_DATA_TYPE_NUMBER = 'number' as const
export const ORDER_PROPERTY_DATA_TYPE_DATETIME = 'datetime' as const
export const ORDER_PROPERTY_DATA_TYPE_CHECKBOX = 'checkbox' as const
export const ORDER_PROPERTY_DATA_TYPE_FILE = 'file' as const
export const ORDER_PROPERTY_DATA_TYPE_PHOTO = 'photo' as const
export const ORDER_PROPERTY_DATA_TYPE_DECIMAL = 'decimal' as const

export const COLUMN_WIDTH = 180

export const MEDIA_DIRECTORY = {
  COMMENTS: 'comments',
  USERS: 'users',
  DOCUMENTS: 'documents',
  ORDERS: 'orders',
  OTHERS: 'others',
}

export const COLLAB_ROLE = [
  {
    label: 'Owner',
    value: COLLABORATOR_ROLE_OWNER,
    description: 'Can fully edit order and invite users',
  },
  {
    label: 'Editor',
    value: COLLABORATOR_ROLE_EDITOR,
    description:
      'Can edit order with limitations and invite users from his/her company',
  },
  {
    label: 'Doer',
    value: COLLABORATOR_ROLE_DOER,
    description: 'Can edit tasks only',
  },
  {
    label: 'Updater',
    value: COLLABORATOR_ROLE_UPDATER,
    description: 'Can partially edit assigned tasks',
  },
  {
    label: 'Viewer',
    value: COLLABORATOR_ROLE_VIEWER,
    description: 'Can comment only',
  },
  {
    label: 'Remove',
    value: COLLABORATOR_ROLE_REMOVE,
    description: '',
  },
]

export const COLLAB_ROLE_ADMIN_ONLY = [
  {
    label: 'Owner',
    value: 'owner',
    description: 'Can fully edit order and invite users',
  },
  {
    label: 'Remove',
    value: 'remove',
    description: '',
  },
]

export const COLLAB_ROLE_EXTERNAL = [
  {
    label: 'Editor',
    value: 'editor',
    description:
      'Can edit order with limitations and invite users from his/her company',
  },
  {
    label: 'Doer',
    value: 'doer',
    description: 'Can edit tasks only',
  },
  {
    label: 'Updater',
    value: 'updater',
    description: 'Can partially edit assigned tasks',
  },
  {
    label: 'Viewer',
    value: 'viewer',
    description: 'Can comment only',
  },
  {
    label: 'Remove',
    value: 'remove',
    description: '',
  },
]

export const PROJECT_CONTROL_TYPE = [
  {
    label: 'Plan Submit Approve',
    value: 'Plan Submit Approve',
  },
]

export const FIELD_INPUT_TYPE_ARRAY = [
  'text',
  'number',
  'select',
  'date',
  'file',
  'image',
]

export const FIELD_INPUT_TYPE = {
  TEXT: ORDER_PROPERTY_DATA_TYPE_TEXT,
  STRING: ORDER_PROPERTY_DATA_TYPE_STRING,
  NUMBER: ORDER_PROPERTY_DATA_TYPE_NUMBER,
  DATETIME: ORDER_PROPERTY_DATA_TYPE_DATETIME,
  CHECKBOX: ORDER_PROPERTY_DATA_TYPE_CHECKBOX,
  FILE: ORDER_PROPERTY_DATA_TYPE_FILE,
  PHOTO: ORDER_PROPERTY_DATA_TYPE_PHOTO,
  DECIMAL: ORDER_PROPERTY_DATA_TYPE_DECIMAL,
}

export const DEFAULT_ORDER_DETAIL_FIELDS: Array<IInputProperty> = [
  {
    field: 'buyer',
    name: 'Buyer',
    value: '',
    data_type: FIELD_INPUT_TYPE.TEXT,
    required: false,
  },
  {
    field: 'factory',
    name: 'Factory',
    value: '',
    data_type: FIELD_INPUT_TYPE.TEXT,
    required: false,
  },
  {
    field: 'masterOrder',
    name: 'Master Order',
    value: '',
    data_type: FIELD_INPUT_TYPE.TEXT,
    required: false,
  },
  {
    field: 'country',
    name: 'Country',
    value: '',
    data_type: FIELD_INPUT_TYPE.TEXT,
    required: false,
  },
  {
    field: 'productCategory',
    name: 'Product Category',
    value: '',
    data_type: FIELD_INPUT_TYPE.TEXT,
    required: false,
  },
]

export const DROP_COLUMNS_END = [
  {
    key: 'supplier',
    value: 'pbf.table.my_order.supplier',
    type: 'default',
    displayOrder: 0,
  },
  {
    key: 'factory',
    value: 'pbf.table.my_order.factory',
    displayOrder: 1,
  },
  {
    key: 'country',
    value: 'pbf.table.my_order.country',
    dataSource: 'property_values:country',
    displayOrder: 2,
  },
  {
    key: 'deliveryDate',
    value: 'pbf.table.my_order.delivery_date',
    type: 'default',
    displayOrder: 3,
  },
]

export const DROP_COLUMNS_START = [
  {
    key: 'order_number',
    value: 'pbf.table.my_order.order_number',
    fixed: 'left',
    type: 'default',
    displayOrder: 0,
  },
  {
    key: 'progress_status',
    value: 'pbf.table.my_order.progress_status',
    type: 'default',
    displayOrder: 1,
  },
]

export const DISPLAY_DEFAULT_COLUMN_TABLE = [
  // ...DROP_COLUMNS_START,
  {
    key: 'progress',
    value: 'pbf.table.my_order.progress',
    displayOrder: 0,
  },
  {
    key: 'creator',
    value: 'pbf.table.my_order.creator',
    displayOrder: 1,
  },
  {
    key: 'collaborators',
    value: 'pbf.table.my_order.collaborators',
    displayOrder: 2,
  },
  {
    key: 'action',
    value: 'pbf.table.my_order.action',
    fixed: 'right',
    displayOrder: 3,
  },
]

export const DISPLAY_DEFAULT_COLUMN = [
  'progress',
  'creator',
  'collaborators',
  'action',
]

export type ORDER_PARAMS_TYPE = {
  page_number?: number
  page_size?: number
  filter?: string
  sort?: string
}

export const defaultOrderParams: ORDER_PARAMS_TYPE = {
  page_number: 1,
  page_size: 10,
  filter: 'all',
  sort: '',
}

export const ORDER_QUERY_PARAM_TYPE: (keyof ORDER_PARAMS_TYPE)[] = [
  'page_number',
  'page_size',
  'filter',
  'sort',
]

export type ORDER_PAYLOAD_TYPE = {
  search?: string | null
  delivery_date?: any
  suppliers?: string[] | null
  factories?: string[] | null
  style_ids?: string[] | null
  [key: string]: any
}

export const DEFAULT_ORDER_FILTER: ORDER_PAYLOAD_TYPE = {
  search: null,
  delivery_date: null,
  suppliers: null,
  factories: null,
  style_ids: null,
}

export const ORDER_ADVANCE_FILTER_LISTING: (keyof ORDER_PAYLOAD_TYPE)[] = [
  'search',
  'delivery_date',
  'suppliers',
  'factories',
  'style_ids',
]

export enum PriorityStatus {
  URGENT = '1-urgent',
  NORMAL = '2-normal',
  LOW = '3-low',
}

export const ORDER_PRIORITY_STATUS = {
  '1-urgent': 'Urgent',
  '2-normal': 'Normal',
  '3-low': 'Low',
}

export const ORDER_STATUSES = {
  draft: 'Draft',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  in_progress: 'In Progress',
  completed: 'Completed',
}

export const ORDER_TEXT = {
  searchOrder: 'Order No., Style No., Supplier, Factory',
}

export const ORDER_PRESET_SORTER = [
  {
    field: 'order_number',
    sortValue: 'order.number',
    descend: '-order.number',
    ascend: 'order.number',
  },
  {
    field: 'deliveryDate',
    sortValue: 'order.to_be_delivered_date',
    descend: '-order.to_be_delivered_date',
    ascend: 'order.to_be_delivered_date',
  },
  {
    field: 'progress_status',
    sortValue: 'order.progress_status',
    descend: '-order.progress_status',
    ascend: 'order.progress_status',
  },
  {
    field: 'supplier',
    sortValue: 'supplier.name',
    descend: '-supplier.name',
    ascend: 'supplier.name',
  },
  {
    field: 'factory',
    sortValue: 'factory.name',
    descend: '-factory.name',
    ascend: 'factory.name',
  },
  {
    field: 'progress',
    sortValue: 'order.progress',
    descend: '-order.progress',
    ascend: 'order.progress',
  },
  {
    field: 'country',
    sortValue: 'pv.country',
    descend: '-pv.country',
    ascend: 'pv.country',
  },
]
